import { useMemo } from "react";

/**
 * Hook che calcola i totali stimati di un ordine in corso nella pagina
 * Giri (qty, omaggi, articoli selezionati, totale netto stimato) e il
 * conteggio degli articoli senza prezzo indicato.
 *
 * Logica prezzi (cascata applicata per ogni riga, in ordine di priorità):
 *  1. discount_override manuale sull'item → applica sc1..sc4
 *  2. promo "manual" (prezzo fisso cliente) → esclude sconto canale
 *  3. promo "promo" netto fisso → esclude sconto canale
 *  4. regola sconto cliente (sc1..sc4 da resolve-prices) → applica sconto canale
 *  5. solo listino → applica sconto canale
 *
 * `noPriceCount` segnala gli articoli NON-Triolo selezionati senza alcun
 * prezzo "indicato" — promemoria non bloccante per l'agente.
 */
export function useGiroTotals({
  orderItems,
  stopProducts,
  extraProducts,
  selectedStop,
  clientDiscounts,
  lockedOrder,
}) {
  const totals = useMemo(() => {
    let q = 0, om = 0, count = 0;
    let subtotApplica = 0;
    let subtotEsclude = 0;
    let totalCost = 0;  // Costo totale del giro (per il calcolo del margine)
    const allProducts = [...stopProducts, ...extraProducts];
    const sc = Number(selectedStop?.sconto_canale || 0);
    for (const [cod, e] of Object.entries(orderItems)) {
      const qv = e?.qty || 0;
      const ov = e?.qty_om || 0;
      if (qv > 0 || ov > 0) count += 1;
      q += qv;
      om += ov;
      if (qv > 0 || ov > 0) {
        const prod = allProducts.find((p) => p.cod_articolo === cod);
        const pPurch = Number(prod?.price_purchase || 0);
        // Il costo include sia gli articoli venduti CHE gli omaggi
        // (gli omaggi pesano sul margine come voce di costo pura)
        totalCost += pPurch * (qv + ov);
      }
      if (qv > 0) {
        const prod = allProducts.find((p) => p.cod_articolo === cod);
        const price = Number(prod?.price_list || 0);
        const ovr = e?.discount_override;
        const cd = clientDiscounts[cod];
        let rowNetU = price;
        let escludeCanale = false;
        if (ovr) {
          for (const d of [ovr.sc1, ovr.sc2, ovr.sc3, ovr.sc4]) {
            if (d && d > 0) rowNetU *= (1 - d / 100);
          }
        } else if (cd?.source === "manual" && cd?.manual_price > 0) {
          rowNetU = cd.manual_price;
          escludeCanale = true;
        } else if (cd?.source === "promo" && cd?.promo_net > 0) {
          rowNetU = cd.promo_net;
          escludeCanale = true;
        } else if (cd) {
          for (const d of [cd.sc1, cd.sc2, cd.sc3, cd.sc4]) {
            if (d && d > 0) rowNetU *= (1 - d / 100);
          }
        }
        if (escludeCanale) subtotEsclude += rowNetU * qv;
        else subtotApplica += rowNetU * qv;
      }
    }
    const stima = (sc > 0 ? subtotApplica * (1 - sc / 100) : subtotApplica) + subtotEsclude;
    const marginEur = stima - totalCost;
    const marginPct = stima > 0 ? (marginEur / stima * 100) : 0;
    return { totalQty: q, totalOm: om, selectedCount: count, totalStima: stima,
             totalCost, marginEur, marginPct };
  }, [orderItems, stopProducts, extraProducts, selectedStop, clientDiscounts]);

  const noPriceCount = useMemo(() => {
    if (lockedOrder) return 0;
    let n = 0;
    const allProducts = [...stopProducts, ...extraProducts];
    const canale = Number(selectedStop?.sconto_canale || 0);
    for (const [cod, e] of Object.entries(orderItems)) {
      // Solo articoli con qta vendita > 0: gli omaggi puri non richiedono
      // conferma prezzo (non vengono fatturati).
      if (!((e?.qty || 0) > 0)) continue;
      const prod = allProducts.find((p) => p.cod_articolo === cod);
      if (!prod) continue;
      const isTriolo = String(prod.macro_category || "").trim().toLowerCase() === "prodotti triolo";
      if (isTriolo) continue;
      const ovr = e?.discount_override;
      const hasOverride = !!ovr && ((ovr.sc1 || 0) + (ovr.sc2 || 0) + (ovr.sc3 || 0) + (ovr.sc4 || 0)) > 0;
      const cd = clientDiscounts[cod];
      const isManual = cd?.source === "manual" && (cd?.manual_price || 0) > 0;
      const isPromo = cd?.source === "promo";
      const hasRule = !!cd && cd.source !== "manual" && ((cd.sc1 || 0) + (cd.sc2 || 0) + (cd.sc3 || 0) + (cd.sc4 || 0)) > 0;
      // Ultimo prezzo del cliente >= netto base (listino × canale) → margine pieno: niente warning
      const ultimoPrezzo = Number(prod.ultimo_prezzo || 0);
      const priceList = Number(prod.price_list || 0);
      const nettoBase = priceList * (canale > 0 ? (1 - canale / 100) : 1);
      const tolerance = Math.max(0.05, nettoBase * 0.005);
      const lastAtFullMargin = ultimoPrezzo > 0 && ultimoPrezzo >= (nettoBase - tolerance);
      if (!(hasOverride || isManual || isPromo || hasRule || lastAtFullMargin)) n += 1;
    }
    return n;
  }, [orderItems, stopProducts, extraProducts, clientDiscounts, lockedOrder, selectedStop]);

  return { ...totals, noPriceCount };
}
